import { Stack } from '@mui/material'
import { CheckIcon } from '@phosphor-icons/react'
import { Image, InfoText, Modal, tokens, User } from 'ComponentLibrary'
import { Typography } from 'ComponentLibrary/src/piazza'
import { Checkbox } from 'ComponentLibrary/src/piazza/selectors'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { type AppraiserSelectionSectionCopy, AppraiserSelectionSection } from './AppraiserSelectionSection'

type ApprovalModalProps = {
    isApprovalOpen: boolean
    onClose: () => void
    onConfirm: () => void
    hasNewAnnotations: boolean
    commentsAreAlreadyVisible?: boolean
    isPersisting?: boolean
    hasAppraiserInObservers?: boolean
    hasAppraiserInObserversOrParticipants?: boolean
    appraiserUsers: User[]
    selectedAppraiserId?: number
    setSelectedAppraiserId: (selectedId?: number) => void
    isAppraiserUsersPending?: boolean
    copy?: Partial<ApprovalModalCopy>
}

export type ApprovalModalCopy = {
    title: string
    close: string
    primaryButton: string
    secondaryButton: string
    imageAlt: string
    makeAnnotationsVisible: string
    requestFinalizationWithAnnotations: string
    requestFinalization: string
    infoText: string
    confirmationLabel: string
    appraiserSelection: Partial<AppraiserSelectionSectionCopy>
}

export function ApprovalModal({
    isApprovalOpen,
    onClose,
    onConfirm,
    hasNewAnnotations,
    commentsAreAlreadyVisible,
    isPersisting,
    hasAppraiserInObservers,
    hasAppraiserInObserversOrParticipants,
    appraiserUsers,
    selectedAppraiserId,
    setSelectedAppraiserId,
    isAppraiserUsersPending,
    copy: copyOverrides,
}: ApprovalModalProps) {
    const { t: tCommonActions } = useTranslation('terminology', { keyPrefix: 'common.actions' })
    const { t: tConfirmation } = useTranslation('terminology', { keyPrefix: 'common.confirmation' })
    const { t: tPlausibility } = useTranslation('terminology', { keyPrefix: 'plausibility' })
    const { t: tAppraiserSelectionSection } = useTranslation('pages', {
        keyPrefix: 'assets.assetDetails.valuation.valuationPlausibility.appraiserSelectionSection',
    })
    const { t: tApprovalModal } = useTranslation('pages', {
        keyPrefix: 'assets.assetDetails.valuation.valuationPlausibility.approvalModal',
    })
    const copy: ApprovalModalCopy = {
        title: copyOverrides?.title ?? tPlausibility('approveDraft'),
        close: copyOverrides?.close ?? tCommonActions('close'),
        primaryButton: copyOverrides?.primaryButton ?? tCommonActions('approve'),
        secondaryButton: copyOverrides?.secondaryButton ?? tConfirmation('cancel'),
        imageAlt: copyOverrides?.imageAlt ?? tPlausibility('approvalImageAlt'),
        makeAnnotationsVisible: copyOverrides?.makeAnnotationsVisible ?? tPlausibility('makeAnnotationsVisible'),
        requestFinalizationWithAnnotations:
            copyOverrides?.requestFinalizationWithAnnotations ?? tApprovalModal('items.requestFinalisationAfterComments'),
        requestFinalization: copyOverrides?.requestFinalization ?? tApprovalModal('items.requestFinalisation'),
        infoText: copyOverrides?.infoText ?? tApprovalModal('warningText'),
        confirmationLabel: copyOverrides?.confirmationLabel ?? tApprovalModal('confirmationLabel'),
        appraiserSelection: {
            title: tAppraiserSelectionSection('title'),
            description: tAppraiserSelectionSection('description'),
            loading: tAppraiserSelectionSection('loading'),
            empty: tAppraiserSelectionSection('emptyState'),
            selectLabel: tAppraiserSelectionSection('fieldLabel'),
            ...copyOverrides?.appraiserSelection,
        },
    }
    const hasResponsibleAppraiser = hasAppraiserInObservers ?? hasAppraiserInObserversOrParticipants ?? false
    const [confirmApproval, setConfirmApproval] = useState(false)
    const hasAppraiserOptions = appraiserUsers.length > 0
    const hasSelectedAppraiser = selectedAppraiserId !== undefined

    return (
        <Modal
            title={copy.title}
            isOpen={isApprovalOpen}
            onClose={onClose}
            showCloseIcon
            closeIconTooltip={copy.close}
            footerProps={{
                primaryButton: {
                    text: copy.primaryButton,
                    onClick: onConfirm,
                    disabled:
                        isPersisting ||
                        !confirmApproval ||
                        (!hasResponsibleAppraiser && isAppraiserUsersPending) ||
                        (!hasResponsibleAppraiser && hasAppraiserOptions && !hasSelectedAppraiser),
                },
                secondaryButton: { text: copy.secondaryButton, onClick: onClose },
            }}
        >
            <Stack direction="column" gap={tokens.SizingSpacing300} width="100%">
                <Stack
                    alignItems="center"
                    justifyContent="center"
                    paddingY={tokens.SizingSpacing150}
                    bgcolor={tokens.ColorGraphSequentialFiveLightest}
                    width="100%"
                >
                    <Image src="/handoverSuccess.svg" alt={copy.imageAlt} width={135} height={135} />
                </Stack>

                <Stack direction="column" gap={tokens.SizingSpacing150}>
                    {hasNewAnnotations ? (
                        <>
                            {!commentsAreAlreadyVisible && (
                                <Stack direction="row" gap={tokens.SizingSpacing075}>
                                    <CheckIcon
                                        size={tokens.SizingIconS}
                                        weight="bold"
                                        color={tokens.ColorIconDefault}
                                        style={{ flexShrink: 0 }}
                                    />
                                    <Typography variant="textS" color={tokens.ColorTextDefault}>
                                        {copy.makeAnnotationsVisible}
                                    </Typography>
                                </Stack>
                            )}
                            <Stack direction="row" gap={tokens.SizingSpacing075}>
                                <CheckIcon
                                    size={tokens.SizingIconS}
                                    weight="bold"
                                    color={tokens.ColorIconDefault}
                                    style={{ flexShrink: 0 }}
                                />
                                <Typography variant="textS" color={tokens.ColorTextDefault}>
                                    {copy.requestFinalizationWithAnnotations}
                                </Typography>
                            </Stack>
                        </>
                    ) : (
                        <Stack direction="row" gap={tokens.SizingSpacing075}>
                            <CheckIcon size={tokens.SizingIconS} weight="bold" color={tokens.ColorIconDefault} style={{ flexShrink: 0 }} />
                            <Typography variant="textS" color={tokens.ColorTextDefault}>
                                {copy.requestFinalization}
                            </Typography>
                        </Stack>
                    )}

                    <InfoText size="small" variant="error" text={copy.infoText} />
                </Stack>

                <Checkbox
                    label={copy.confirmationLabel}
                    checked={confirmApproval}
                    onChange={() => setConfirmApproval((previous) => !previous)}
                />

                {!hasResponsibleAppraiser && (
                    <AppraiserSelectionSection
                        appraiserUsers={appraiserUsers}
                        selectedAppraiserId={selectedAppraiserId}
                        setSelectedAppraiserId={setSelectedAppraiserId}
                        isAppraiserUsersPending={isAppraiserUsersPending}
                        copy={copy.appraiserSelection}
                    />
                )}
            </Stack>
        </Modal>
    )
}
